import { useState, type FormEvent } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/daho86boh653ivfvo2g0";

const clueTypes = ["事件线索提供", "内容纠错", "引用与授权", "商务合作", "其他事宜"];

export default function ContactForm() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [messageLength, setMessageLength] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const guardValue = String(formData.get("website_alt") ?? "").trim();
    if (guardValue) {
      setStatus("success");
      setFormError("");
      form.reset();
      setMessageLength(0);
      return;
    }

    setStatus("loading");
    setFormError("");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      payload.append(key, String(value));
    });

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText) as Record<string, unknown>;
      } catch {
        parsed = null;
      }

      const code = (parsed?.code as string) ?? "";
      const meta = (parsed?.meta as Record<string, string>) ?? {};
      const serverMsg =
        meta.message || (parsed?.message as string) || meta.detail || (parsed?.detail as string) || "";
      const spamFlag =
        String(serverMsg).toLowerCase().includes("spam") ||
        responseText.toLowerCase().includes("form data is spam");

      if (response.ok && code === "OK" && !spamFlag) {
        setStatus("success");
        form.reset();
        setMessageLength(0);
      } else {
        setStatus("error");
        setFormError(serverMsg || `提交未成功，请稍后重试（状态码 ${response.status}）`);
      }
    } catch {
      setStatus("error");
      setFormError("网络连接异常，请检查网络后重新提交。");
    }
  };

  if (status === "success") {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-background-200 bg-background-50 p-8 text-center md:p-10">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-accent-100 text-2xl text-accent-800">
          <i className="ri-check-line" />
        </span>
        <h3 className="mt-5 font-heading text-lg font-semibold text-foreground-950">已收到你的信息</h3>
        <p className="mt-2 max-w-sm text-sm leading-relaxed text-foreground-600">
          感谢你的线索与建议。编辑部会在 1 至 2 个工作日内查看并评估，如需要补充信息会通过邮件与你联系。
        </p>
        <button
          type="button"
          onClick={() => setStatus("idle")}
          className="mt-5 cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600"
        >
          再提交一条
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      data-readdy-form
      className="relative rounded-lg border border-background-200 bg-background-50 p-5 md:p-7"
    >
      <h3 className="font-heading text-lg font-semibold text-foreground-950">提交线索 / 联系编辑部</h3>
      <p className="mt-2 text-sm leading-relaxed text-foreground-600">
        如果你看到说法混乱、信息互相矛盾的事件，欢迎把公开资料发给我们。
      </p>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="contact-name" className="mb-1.5 block text-sm text-foreground-700">
            称呼
          </label>
          <input
            id="contact-name"
            type="text"
            name="name"
            required
            placeholder="怎么称呼你"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="mb-1.5 block text-sm text-foreground-700">
            邮箱
          </label>
          <input
            id="contact-email"
            type="email"
            name="email"
            required
            placeholder="用于接收回复"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="contact-type" className="mb-1.5 block text-sm text-foreground-700">
          事项类型
        </label>
        <select
          id="contact-type"
          name="clue_type"
          defaultValue={clueTypes[0]}
          className="w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
        >
          {clueTypes.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>
      </div>

      <div className="mt-4">
        <div className="mb-1.5 flex items-center justify-between">
          <label htmlFor="contact-message" className="block text-sm text-foreground-700">
            详细说明
          </label>
          <span className="text-xs text-foreground-400">{messageLength}/500</span>
        </div>
        <textarea
          id="contact-message"
          name="message"
          required
          rows={5}
          maxLength={500}
          onChange={(e) => setMessageLength(e.target.value.length)}
          placeholder="描述事件背景、关键时间点，以及可以公开核对的资料来源链接。"
          className="w-full resize-y rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
        />
      </div>

      <div className="contact-aux" aria-hidden="true">
        <label htmlFor="contact-alt">备用联系渠道</label>
        <input
          id="contact-alt"
          type="text"
          name="website_alt"
          tabIndex={-1}
          autoComplete="off"
          readOnly
        />
      </div>

      {status === "error" && formError ? (
        <p className="mt-4 flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-3.5 py-2.5 text-sm text-primary-700">
          <i className="ri-error-warning-line mt-0.5 text-base" />
          <span>{formError}</span>
        </p>
      ) : null}

      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs leading-relaxed text-foreground-500">
          提交即表示你同意我们使用该信息进行内容核实，我们不会将其用于其他用途。
        </p>
        <button
          type="submit"
          disabled={status === "loading"}
          className="inline-flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
        >
          {status === "loading" ? (
            <>
              <i className="ri-loader-4-line animate-spin text-base" />
              提交中
            </>
          ) : (
            <>
              提交信息
              <i className="ri-send-plane-line text-base" />
            </>
          )}
        </button>
      </div>
    </form>
  );
}